import * as React from "react";
import {cx} from "emotion";

import "./streets-box.scss";

import {isApiFalse}             from "@Common/logic/is-api-false";
import {isApiTrue}              from "@Common/logic/is-api-true";
import {cs}                     from "@Common/react/chain-services";
import {scc}                    from "@Common/react/state-cascade-change";
import {stateChangeNestedValue} from "@Common/react/state-change-nested-value";
import {UseState}               from "@Common/react/use-state";
import {isDevQuan}              from "@Common/ui-components/debug/is-dev-quan";
import {plural}                 from "@Common/ui-components/language/plural";
import {ErrorHighlighter}       from "@Common/ui-components/light/error-highlighter";
import {sum}                    from "@Common/utils/collections";
import {chain}                  from "@Common/utils/fs";
import {values}                 from "@Common/utils/objects";

import {EditActionInTable} from "../../../../../../../common/design2/data-table-ui/edit-action-in-table/edit-action-in-table";
import {PageList}          from "../../../../../../../common/design2/pagination-ui/page-list/page-list";
import {SearchInput}       from "../../../../../../../common/design2/search-input/search-input";
import {getStreet}         from "../common/get-street";

import {Table} from "./table/table";

export const StreetsBox = ({next: rootNext}) => cs(
    ["eh", (_, next) => ErrorHighlighter({next})],

    ["data", (_, next) => UseState({

        ...isDevQuan() && {
            initValue: require("./sample-radius-search-response"),
        },
        next,
    })],

    ({eh, data}) => rootNext({
        hasData: !!data.value,
        setStreets: (d) => data.onChange(d),
        data: data.value,

        getExcludedStreets: () => Object.keys(data.value.streets).filter((street) => isApiFalse(data.value.streets[street].addresses[street].display_address)),

        containsAddresses: ({getExcludedAddrCounts}) => (
            data.value?.streets &&
            sum(Object["values"](data.value.streets), (r) => {
                const street = getStreet(r);
                return !isApiTrue(street.display_address) ? 0 : (
                    +street.count - sum(Object["values"](getExcludedAddrCounts(street)))
                );
            })
        ),

        highlightErrors: () => {
            eh.start();
        },

        getStreetRecord: (street) => data.value.streets[street],
        getStreetRecords: () => values(data.value.streets),
        getStreetNames: () => Object.keys(data.value.streets),

        render: ({getExcludedCounts, searchState, }) => cs(
            ["filter", (_, next) => UseState({next})],
            ["match", ({filter}, next) => next(!filter.value? (() => true) : (() => {
                const lowerCase = filter.value.toLowerCase();
                return (street) => (
                    street.address.toLowerCase().includes(lowerCase) ||
                    street.zip_code.startsWith(lowerCase)
                );
            })())],
            ["records", ({}, next) => next(data.value?.streets && Object["values"](data.value.streets))],
            ["filteredRecords", ({records, match}, next) => next(chain(
                records,
                (_) => _?.filter((r) => match(getStreet(r))),
            ))],
            ({filter, records, filteredRecords, match, }) => (
                <div className={cx("streets-box-8u2", eh.css)}>

                    <div className="controls">
                        {SearchInput({
                            state: filter,
                            placeholder: "Search Street",
                            text: filter.value && <>Found <b>{filteredRecords.length}</b> {plural("street", filteredRecords.length)}</>,
                            xdQaName: "Build Radius List Search Street"
                        })}
                    </div>

                    {Table({
                        count: {
                            selected: records.filter((r) => isApiTrue(getStreet(r).display_address)).length,
                            total: records.length,
                        },
                        masterCheckbox: (
                            !filteredRecords.find((r) => isApiTrue(getStreet(r).display_address)) ? {
                                checked: false,
                                onClick: () => scc(data, "streets[*].addresses[*].display_address", (_, {}, {3: street}) => match(street) ? "1" : _),
                            } : filteredRecords.find((r) => !isApiTrue(getStreet(r).display_address)) ? {
                                checked: "midway",
                                onClick: () => scc(data, "streets[*].addresses[*].display_address", (_, {}, {3: street}) => match(street) ? "1" : _),
                            } : {
                                checked: true,
                                onClick: () => scc(data, "streets[*].addresses[*].display_address", (_, {}, {3: street}) => match(street) ? "0" : _),
                            }

                        ),
                        filteredRecords,
                        renderActions: (street) => (
                            EditActionInTable({
                                active: searchState.value?.type === "street" && searchState.value.address === street.address,
                                onClick: () => searchState.onChange({type: "street", address: street.address}),
                                xdQaName: "Build Radius List "+street.address.split("^_^")[0],
                            })
                        ),
                        getExcludedCounts,
                        setStreetSelected: (street, selected) => stateChangeNestedValue(data, ["streets", street.address, "addresses", street.address, "display_address"], () => selected),
                        getFooter: ({pagination}) => (
                            <div className="footer-4ii">
                                <div className="summary">
                                    <table>
                                        <tbody>
                                        <tr className="total-addresses">
                                            <td className="label" xd-qa-type="label" xd-qa-name="Build Radius List Total Addresses">
                                                Total Addresses
                                            </td>
                                            <td className="value" xd-qa-type="label" xd-qa-name="Build Radius List Total Addresses Amount">
                                                {getSumAddresses(data, getExcludedCounts)}
                                            </td>
                                            {getSumDnmCount(data, getExcludedCounts) > 0 && <td className="label danger" xd-qa-type="label" xd-qa-name="Total Do Not Mail">
                                                {`(Excluding ${getSumDnmCount(data, getExcludedCounts)} Do Not Mail)`}
                                            </td>}
                                        </tr>
                                        <tr className="total-streets">
                                            <td className="label" xd-qa-type="label" xd-qa-name="Build Radius List Total Streets">
                                                Total Streets
                                            </td>
                                            <td className="value" xd-qa-type="label" xd-qa-name="Build Radius List Total Streets Amount">
                                                {sum(Object["values"](data.value.streets), (r) => {
                                                    const street = getStreet(r);
                                                    return !isApiTrue(street.display_address) ? 0 : 1;
                                                })}
                                            </td>
                                        </tr>
                                        </tbody>
                                    </table>
                                </div>

                                {pagination.active && (
                                    <div className="pagination">
                                        {PageList({...pagination, xdQaNamePrefix: "Build Radius List Street"})}
                                    </div>
                                )}

                                <div className="actions">
                                    <button {...{
                                        disabled: searchState.value?.type === "all",
                                        onClick: () => searchState.onChange({type: "all"}),
                                        "xd-qa-type": "button",
                                        "xd-qa-name": "Build Radius List VIEW ALL ADDRESSES",
                                    }}>
                                        VIEW ALL ADDRESSES
                                    </button>
                                </div>
                            </div>
                        ),
                    })}

                </div>
            ),
        ),
    }),
);

const getSumDnmCount = (data, getExcludedCounts) => {
    return sum(Object["values"](data.value.streets), (r) => {
        const street = getStreet(r);
        return getExcludedCounts(street).dnm;
    })
}

const getSumAddresses = (data, getExcludedCounts) => {
    return sum(Object["values"](data.value.streets), (r) => {
        const street = getStreet(r);
        return !isApiTrue(street.display_address) ? 0 : (
            +street.count - sum(Object["values"](getExcludedCounts(street)))
        );
    })
}